<script setup lang="ts">
import { inject, reactive, type Ref } from 'vue';
import type { Lab } from '@/services/api/emsl';

interface DialogRefValue {
  data?: { item?: Lab };
}

const dialogRef = inject<Ref<DialogRefValue | undefined>>('dialogRef');
const emit = defineEmits<{ (e: 'close', data?: unknown): void }>();

const item = dialogRef?.value?.data?.item;

const form = reactive<Partial<Lab>>({
  name: item?.name ?? '',
  emsl_lab_id: item?.emsl_lab_id ?? '',
  address_1: item?.address_1 ?? '',
  address_2: item?.address_2 ?? '',
  city: item?.city ?? '',
  state: item?.state ?? '',
  zip: item?.zip ?? '',
  telephone: item?.telephone ?? '',
  email: item?.email ?? '',
});

function submit(): void {
  if (!form.name) return;
  emit('close', { ...form });
}
</script>

<template>
  <form class="lab-dialog" @submit.prevent="submit">
    <div class="lab-dialog__grid">
      <div class="lab-dialog__field lab-dialog__field--wide">
        <label for="lab-name">Name *</label>
        <input id="lab-name" v-model="form.name" type="text" required autofocus />
      </div>
      <div class="lab-dialog__field">
        <label for="lab-emsl-id">EMSL Lab ID</label>
        <input id="lab-emsl-id" v-model="form.emsl_lab_id" type="text" />
      </div>
      <div class="lab-dialog__field lab-dialog__field--wide">
        <label for="lab-addr1">Address 1</label>
        <input id="lab-addr1" v-model="form.address_1" type="text" />
      </div>
      <div class="lab-dialog__field lab-dialog__field--wide">
        <label for="lab-addr2">Address 2</label>
        <input id="lab-addr2" v-model="form.address_2" type="text" />
      </div>
      <div class="lab-dialog__field">
        <label for="lab-city">City</label>
        <input id="lab-city" v-model="form.city" type="text" />
      </div>
      <div class="lab-dialog__field lab-dialog__field--small">
        <label for="lab-state">State</label>
        <input id="lab-state" v-model="form.state" type="text" maxlength="2" />
      </div>
      <div class="lab-dialog__field lab-dialog__field--small">
        <label for="lab-zip">Zip</label>
        <input id="lab-zip" v-model="form.zip" type="text" />
      </div>
      <div class="lab-dialog__field">
        <label for="lab-phone">Phone</label>
        <input id="lab-phone" v-model="form.telephone" type="text" />
      </div>
      <div class="lab-dialog__field lab-dialog__field--wide">
        <label for="lab-email">Email</label>
        <input id="lab-email" v-model="form.email" type="email" />
      </div>
    </div>
    <div class="lab-dialog__actions">
      <button type="submit">Save</button>
      <button type="button" @click="emit('close')">Cancel</button>
    </div>
  </form>
</template>

<style scoped>
.lab-dialog {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 0.25rem 0 0.5rem;
  min-width: 28rem;
}
.lab-dialog__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.875rem;
}
.lab-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.lab-dialog__field--wide {
  grid-column: 1 / -1;
}
.lab-dialog__field--small {
  grid-column: span 1;
}
.lab-dialog__field label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}
.lab-dialog__field input {
  padding: 0.4rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font: inherit;
}
.lab-dialog__actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
</style>
